import {Buffer} from 'buffer';

import axios, {AxiosRequestConfig, AxiosResponse} from 'axios';
import {TFunction} from 'i18next';
import {endsWith, startsWith} from 'lodash';

import {STATUS_CODES} from 'constants/http';
import {ValidationCode} from 'enums';
import {TRANSLATION_KEYS} from 'locale';
import {ApiError, FieldValidationError} from 'types';

const isValidationError = (error: {response: AxiosResponse}) => error?.response?.status === STATUS_CODES.UNPROCESSABLE_ENTITY;

const isTooManyRequestsError = (error: ApiError) => error?.response?.status === STATUS_CODES.TOO_MANY_REQUESTS;

const getResponseErrors = (error: {response: AxiosResponse}) => {
    if (isValidationError(error)) {
        return null;
    }

    if (error
        && error.response
        && error.response.data
        && Array.isArray(error.response.data.errors)
    ) {
        return error.response.data.errors.join(' ');
    }
    return 'Internal server error';
};

const isResourceNotFoundError = (error: {response: AxiosResponse}) => {
    const errors = getResponseErrors(error);
    return startsWith(errors, 'Resource') && endsWith(errors, 'was not found.');
};

const getValidationErrors = (error: {response: AxiosResponse}) => {
    if (!isValidationError(error)) {
        return null;
    }
    return error.response.data;
};

const isNotFoundError = (error: ApiError) => error?.response?.status === STATUS_CODES.NOT_FOUND;

const isFileTypeError = (error: ApiError) => error?.response?.data?.errors?.extension?.code === ValidationCode.InList;

const withExpectedErrors = [
    ValidationCode.Invalid,
    ValidationCode.InvalidFormat,
    ValidationCode.MinLength,
    ValidationCode.MaxLength,
    ValidationCode.MinDate,
    ValidationCode.MaxDate,
    ValidationCode.MaxCount,
    ValidationCode.MaxSize,
    ValidationCode.InList,
    ValidationCode.MinItems,
    ValidationCode.MaxItems,
    ValidationCode.InvalidPattern,
    ValidationCode.Minimum,
    ValidationCode.Maximum,
];

const getErrorMessageByCode = (t: TFunction, validationError: FieldValidationError, overrides = {}) => {
    if (!validationError) {
        return;
    }
    const {code, message, expected} = validationError;
    const T_KEY = overrides[code] || TRANSLATION_KEYS.COMMON.ERROR[`CODE_${code}`];

    if (T_KEY) {
        // TODO: translate expected values (e.g. formats like phone, email etc)
        return withExpectedErrors.includes(code) && expected && !overrides[code] ? `${t(T_KEY)}: ${expected}` : t(T_KEY) ;
    }
    return message;
};

const getValidationCodeTKey = (t: TFunction, code: ValidationCode, overrides = {}) => {
    if (!code) {
        return;
    }
    const T_KEY = overrides[code] || TRANSLATION_KEYS.COMMON.ERROR[`CODE_${code}`];
    return t(T_KEY);
};

const getFieldValidationError = (error: ApiError, field: string): FieldValidationError =>
    error?.response?.data?.errors?.[field];

const getFieldValidationErrorMessage = (error: ApiError, t: TFunction, field: string, overrides = {}) =>
    getErrorMessageByCode(t, getFieldValidationError(error, field), overrides);

const getFieldsValidationErrorMessages = <T extends string>(
    error: ApiError,
    t: TFunction,
    fields: {
        field: T,
        overrides?: {[key: string]: string}
    }[]): Record<T, string> => fields.reduce((acc, cur) => {
        acc[cur.field] = getFieldValidationErrorMessage(error, t, cur.field, cur.overrides);
        return acc;
    }, {} as Record<T, string>);

const getInputValidationErrorProps = (error: ApiError, t: TFunction, field: string, overrides = {}) => {
    const message = getFieldValidationErrorMessage(error, t, field, overrides);
    return message ? {helperText: message, error: true} : {};
};

const saveBlob = (fileName: string, data: BlobPart[], blobOptions?: BlobPropertyBag) => {
    const url = window.URL.createObjectURL(new Blob(data, blobOptions));
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
};

const saveFileFromBase64 = (fileName: string, base64: string) => {
    const buffer = Buffer.from(base64, 'base64');
    return saveBlob(fileName, [buffer]);
};

const downloadFile = async (config: AxiosRequestConfig, fileName: string) => {
    const response = await axios({
        ...config,
        responseType: 'blob',
    });

    saveBlob(fileName, [response.data]);
    return response.data;
};

export const apiUtils = {
    isValidationError,
    isTooManyRequestsError,
    getResponseErrors,
    getValidationErrors,
    isNotFoundError,
    isFileTypeError,
    saveBlob,
    getErrorMessageByCode,
    getValidationCodeTKey,
    getFieldValidationError,
    getFieldValidationErrorMessage,
    getFieldsValidationErrorMessages,
    getInputValidationErrorProps,
    saveFileFromBase64,
    downloadFile,
    isResourceNotFoundError
};